import {
  PAYZHE_BTP_AUTH_TOKEN_KEY,
  PAYZHE_BTP_HTTP_BASE,
  PAYZHE_BTP_PROBE_TIMEOUT_MS,
  PAYZHE_BTP_REQUEST_TIMEOUT_MS,
} from './config';
import { normalizeStatusPayload, normalizeVersionInfo, normalizeJobPayload, normalizeLastWriteDiagnostics, unwrapApiPayload } from './apiEnvelope';
import { getOrCreateZeettuClientId } from './bluetoothProviderSettings';
import {
  isTerminalPayzheBtpJobStatus,
  notifyPayzheBtpJobTerminal,
  registerPayzheBtpJobWait,
} from './payzheBtpJobWait';
import type {
  PayzheBtpApiMessage,
  PayzheBtpDiagnosticsPayload,
  PayzheBtpJobPayload,
  PayzheBtpLogEntry,
  PayzheBtpPrintResult,
  PayzheBtpQueuePayload,
  PayzheBtpSettingsPayload,
  PayzheBtpStatusPayload,
  PayzheBtpVersionInfo,
} from './types';
import { ZEETTU_BRIDGE_VERSION } from './bridgeVersion';
import { printerDebug, printerDebugWarn } from '../printerDebug';

/** Bridge protocol version sent to payzhe BTP for compatibility checks. */
export function getPayzheBtpWebVersion(): string {
  const bridge = String(import.meta.env.VITE_ZEETTU_BRIDGE_VERSION ?? '').trim();
  if (bridge) return bridge;
  const app = String(import.meta.env.VITE_APP_VERSION ?? '').trim();
  if (app && app !== 'web') return app;
  return ZEETTU_BRIDGE_VERSION;
}

/** Zeettu deploy/marketing version for HTTP User-Agent (optional). */
export function getZeettuAppVersionLabel(): string {
  const app = String(import.meta.env.VITE_APP_VERSION ?? '').trim();
  if (app) return app;
  return `bridge/${getPayzheBtpWebVersion()}`;
}

function versionQuery(): string {
  return `?webVersion=${encodeURIComponent(getPayzheBtpWebVersion())}`;
}

export class NativeBtpClientError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'NativeBtpClientError';
  }
}

export type PayzheBtpHandshakePayload = {
  token: string;
  applicationId: string;
  httpPort: number;
  wsPort: number;
};

export type PayzheBtpReachabilityReason =
  | 'not_running'
  | 'auth'
  | 'timeout'
  | 'unknown'
  | 'browser_blocked'
  | 'origin_denied'
  | 'connection_refused';

export type PayzheBtpReachability = {
  reachable: boolean;
  token?: string;
  version?: PayzheBtpVersionInfo;
  reason?: PayzheBtpReachabilityReason;
};

export function reachabilityReasonToErrorKey(reason?: PayzheBtpReachabilityReason): string {
  switch (reason) {
    case 'browser_blocked':
      return 'bluetoothPrinter.payzheBtp.errors.browserBlocked';
    case 'connection_refused':
      return 'bluetoothPrinter.payzheBtp.errors.connectionRefused';
    case 'origin_denied':
      return 'bluetoothPrinter.payzheBtp.errors.originDenied';
    case 'timeout':
      return 'bluetoothPrinter.payzheBtp.errors.timeout';
    case 'auth':
      return 'bluetoothPrinter.payzheBtp.errors.bridgeError';
    default:
      return 'bluetoothPrinter.payzheBtp.errors.connectionRefused';
  }
}

function classifyFetchFailure(): PayzheBtpReachabilityReason {
  if (typeof document !== 'undefined') {
    const origin = document.location?.origin ?? '';
    if (
      origin.startsWith('https://') &&
      !origin.includes('localhost') &&
      !origin.includes('127.0.0.1')
    ) {
      return 'browser_blocked';
    }
  }
  return 'connection_refused';
}

function reasonFromClientError(error: NativeBtpClientError): PayzheBtpReachabilityReason {
  if (error.status === 401) return 'auth';
  if (error.status === 403) return 'origin_denied';
  if (error.message === 'bluetoothPrinter.payzheBtp.errors.timeout') return 'timeout';
  if (error.message.startsWith('bluetoothPrinter.payzheBtp.errors.')) {
    if (error.message.includes('browserBlocked')) return 'browser_blocked';
    if (error.message.includes('connectionRefused')) return 'connection_refused';
    if (error.message.includes('originDenied')) return 'origin_denied';
  }
  if (!error.status) return classifyFetchFailure();
  return 'unknown';
}

export type PayzheBtpPrinterListPayload = {
  recent: Array<{
    id: string;
    name: string;
    address: string;
    transport: string;
    paired: boolean;
    rssi?: number | null;
    modelId?: string | null;
    lastConnectedAt?: string | null;
  }>;
  discovered: Array<{
    id: string;
    name: string;
    address: string;
    transport: string;
    paired: boolean;
    rssi?: number | null;
    modelId?: string | null;
  }>;
  paired: Array<{
    id: string;
    name: string;
    address: string;
    transport: string;
    paired: boolean;
  }>;
  current: { id: string; name: string; transport: string; connectedAt?: string } | null;
};

export function getStoredPayzheBtpToken(): string {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return '';
  try {
    return globalThis.localStorage.getItem(PAYZHE_BTP_AUTH_TOKEN_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

export function savePayzheBtpToken(token: string): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    const trimmed = token.trim();
    if (trimmed) {
      globalThis.localStorage.setItem(PAYZHE_BTP_AUTH_TOKEN_KEY, trimmed);
    } else {
      globalThis.localStorage.removeItem(PAYZHE_BTP_AUTH_TOKEN_KEY);
    }
  } catch {
    /* ignore */
  }
}

async function btpFetchOnce<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    token?: string;
    auth?: boolean;
    timeoutMs?: number;
  } = {},
): Promise<T> {
  const token = options.token ?? getStoredPayzheBtpToken();
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? PAYZHE_BTP_REQUEST_TIMEOUT_MS;
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Zeettu-Client-Id': getOrCreateZeettuClientId(),
      'User-Agent': `Zeettu/${getZeettuAppVersionLabel()} (React; bridge=${getPayzheBtpWebVersion()})`,
    };
    const useAuth = options.auth !== false;
    if (useAuth && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(`${PAYZHE_BTP_HTTP_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
    const text = await response.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text) as unknown;
      } catch {
        parsed = { raw: text };
      }
    }
    if (!response.ok) {
      let message = `payzhe BTP HTTP ${response.status}`;
      let code: string | undefined;
      if (typeof parsed === 'object' && parsed !== null) {
        const envelope = parsed as {
          error?: { message?: string; code?: string } | string;
          message?: string;
        };
        if (typeof envelope.error === 'object' && envelope.error?.message) {
          message = envelope.error.message;
          code = envelope.error.code;
        } else if (typeof envelope.error === 'string') {
          message = envelope.error;
        } else if (typeof envelope.message === 'string') {
          message = envelope.message;
        }
      }
      if (response.status === 403) {
        throw new NativeBtpClientError('bluetoothPrinter.payzheBtp.errors.originDenied', 403);
      }
      const err = new NativeBtpClientError(message, response.status);
      if (code) Object.assign(err, { code });
      throw err;
    }

    return unwrapApiPayload<T>(parsed);
  } catch (e) {
    if (e instanceof NativeBtpClientError) throw e;
    if (e instanceof Error && e.name === 'AbortError') {
      throw new NativeBtpClientError('bluetoothPrinter.payzheBtp.errors.timeout');
    }
    throw new NativeBtpClientError(reachabilityReasonToErrorKey(classifyFetchFailure()));
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

async function btpFetch<T>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    token?: string;
    auth?: boolean;
    timeoutMs?: number;
  } = {},
): Promise<T> {
  const useAuth = options.auth !== false;
  try {
    return await btpFetchOnce<T>(path, options);
  } catch (error) {
    if (useAuth && error instanceof NativeBtpClientError && error.status === 401) {
      savePayzheBtpToken('');
      const handshake = await handshakePayzheBtp();
      savePayzheBtpToken(handshake.token);
      return btpFetchOnce<T>(path, { ...options, token: handshake.token });
    }
    throw error;
  }
}

export async function handshakePayzheBtp(): Promise<PayzheBtpHandshakePayload> {
  return btpFetchOnce<PayzheBtpHandshakePayload>('/auth/handshake', {
    auth: false,
    timeoutMs: 6_000,
  });
}

export async function probePayzheBtpReachable(): Promise<PayzheBtpReachability> {
  try {
    const handshake = await handshakePayzheBtp();
    const version = await btpFetchOnce<unknown>(`/version${versionQuery()}`, {
      token: handshake.token,
      timeoutMs: PAYZHE_BTP_PROBE_TIMEOUT_MS,
    });
    return {
      reachable: true,
      token: handshake.token,
      version: normalizeVersionInfo(version),
    };
  } catch (error) {
    if (error instanceof NativeBtpClientError) {
      return { reachable: false, reason: reasonFromClientError(error) };
    }
    return { reachable: false, reason: classifyFetchFailure() };
  }
}

export async function probePayzheBtpVersion(token?: string): Promise<PayzheBtpVersionInfo> {
  const raw = await btpFetch<unknown>(`/version${versionQuery()}`, { token });
  return normalizeVersionInfo(raw);
}

export async function fetchPayzheBtpStatus(token?: string): Promise<PayzheBtpStatusPayload> {
  const raw = await btpFetch<unknown>('/status', { token });
  return normalizeStatusPayload(raw);
}

export async function fetchPayzheBtpDiagnostics(
  token?: string,
): Promise<PayzheBtpDiagnosticsPayload> {
  const raw = await btpFetch<unknown>('/diagnostics', { token });
  const data = unwrapApiPayload<Record<string, unknown>>(raw);
  return {
    lastWrite: normalizeLastWriteDiagnostics(data.lastWrite),
    bluetooth: (data.bluetooth as Record<string, unknown> | null) ?? null,
    queue: (data.queue as Record<string, unknown> | null) ?? null,
  };
}

export async function fetchPayzheBtpPrinters(token?: string): Promise<PayzheBtpPrinterListPayload> {
  const result = await btpFetch<PayzheBtpPrinterListPayload>('/printers', { token });
  return {
    ...result,
    recent: result.recent ?? [],
  };
}

export async function fetchPayzheBtpLogs(token?: string): Promise<PayzheBtpLogEntry[]> {
  const result = await btpFetch<{ entries?: PayzheBtpLogEntry[] } | PayzheBtpLogEntry[]>('/logs', {
    token,
  });
  if (Array.isArray(result)) return result;
  return result.entries ?? [];
}

export async function postPayzheBtpSettings(
  settings: PayzheBtpSettingsPayload,
  token?: string,
): Promise<PayzheBtpSettingsPayload> {
  await btpFetch<unknown>('/settings', {
    method: 'POST',
    body: settings,
    token,
  });
  return settings;
}

export async function postPayzheBtpPower(
  autoPowerOffMinutes: number,
  token?: string,
): Promise<{ autoPowerOffMinutes: number }> {
  await postPayzheBtpSettings({ autoPowerOffMinutes }, token);
  return { autoPowerOffMinutes };
}

export async function postPayzheBtpConnect(
  body: { printerId: string; transport?: 'auto' | 'ble' | 'classic-serial' },
  token?: string,
): Promise<{ connected: boolean; printerId?: string }> {
  return btpFetch<{ connected: boolean; printerId?: string }>('/connect', {
    method: 'POST',
    body,
    token,
  });
}

export async function postPayzheBtpServiceRestart(token?: string): Promise<void> {
  await btpFetch<unknown>('/service/restart', {
    method: 'POST',
    body: {},
    token,
  });
}

export async function postPayzheBtpDisconnect(token?: string): Promise<{ connected: boolean }> {
  return btpFetch<{ connected: boolean }>('/disconnect', {
    method: 'POST',
    body: {},
    token,
  });
}

export async function postPayzheBtpPrint(
  body: {
    requestId: string;
    payload: { type: 'base64'; data: string };
    printerModel?: string;
    transport?: string;
    encoding?: 'tspl' | 'escpos' | 'raw' | 'base64';
    copies?: number;
    priority?: 'low' | 'normal' | 'high';
  },
  token?: string,
): Promise<PayzheBtpPrintResult> {
  return btpFetch<PayzheBtpPrintResult>('/print', {
    method: 'POST',
    body,
    token,
    timeoutMs: 30_000,
  });
}

export async function fetchPayzheBtpQueue(token?: string): Promise<PayzheBtpQueuePayload> {
  const raw = await btpFetch<unknown>('/queue', { token });
  if (typeof raw !== 'object' || raw === null) {
    return {};
  }
  const data = raw as Record<string, unknown>;
  const jobsRaw = data.jobs;
  const jobs = Array.isArray(jobsRaw)
    ? jobsRaw.map((job) => normalizeJobPayload(job))
    : undefined;
  return {
    pendingCount:
      typeof data.pendingCount === 'number'
        ? data.pendingCount
        : typeof data.queueLength === 'number'
          ? data.queueLength
          : jobs?.filter((j) => !isTerminalPayzheBtpJobStatus(j.status)).length,
    currentJobId:
      typeof data.currentJobId === 'string'
        ? data.currentJobId
        : typeof data.activeJobId === 'string'
          ? data.activeJobId
          : null,
    paused: typeof data.paused === 'boolean' ? data.paused : undefined,
    jobs,
  };
}

export async function fetchPayzheBtpJob(jobId: string, token?: string): Promise<PayzheBtpJobPayload> {
  const raw = await btpFetch<unknown>(`/queue/${encodeURIComponent(jobId)}`, { token });
  return normalizeJobPayload(raw);
}

export type WaitForPayzheBtpJobOptions = {
  timeoutMs?: number;
  pollMs?: number;
  token?: string;
};

export async function waitForPayzheBtpJob(
  jobId: string,
  options: WaitForPayzheBtpJobOptions = {},
): Promise<PayzheBtpJobPayload> {
  const timeoutMs = options.timeoutMs ?? 45_000;
  const pollMs = options.pollMs ?? 800;
  const token = options.token;
  const started = Date.now();

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;

    const finish = (job: PayzheBtpJobPayload) => {
      if (settled) return;
      settled = true;
      if (pollTimer) globalThis.clearInterval(pollTimer);
      unregister();
      resolve(job);
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      if (pollTimer) globalThis.clearInterval(pollTimer);
      unregister();
      reject(error);
    };

    const unregister = registerPayzheBtpJobWait(jobId, (status, lastError) => {
      if (isTerminalPayzheBtpJobStatus(status)) {
        finish({ jobId, status, lastError });
      }
    });

    const poll = async () => {
      if (Date.now() - started >= timeoutMs) {
        fail(new NativeBtpClientError('bluetoothPrinter.payzheBtp.errors.timeout'));
        return;
      }
      try {
        const job = await fetchPayzheBtpJob(jobId, token);
        printerDebug('payzheBtp job poll ok', { jobId, status: job.status });
        notifyPayzheBtpJobTerminal(jobId, job.status, job.lastError);
        if (isTerminalPayzheBtpJobStatus(job.status)) {
          finish(job);
        }
      } catch (pollError) {
        const pollStatus =
          pollError instanceof NativeBtpClientError
            ? pollError.status
            : (pollError as { status?: number }).status;
        printerDebugWarn('payzheBtp job poll failed', {
          jobId,
          error: pollError instanceof Error ? pollError.message : String(pollError),
          status: pollStatus,
        });
        /* keep polling until timeout */
      }
    };

    void poll();
    pollTimer = globalThis.setInterval(() => {
      void poll();
    }, pollMs);
  });
}

/** @deprecated Use fetchPayzheBtpQueue */
export async function fetchPayzheBtpQueueStatus(token?: string): Promise<{
  pendingCount?: number;
  currentJobId?: string | null;
  paused?: boolean;
}> {
  try {
    const queue = await fetchPayzheBtpQueue(token);
    return {
      pendingCount: queue.pendingCount,
      currentJobId: queue.currentJobId,
      paused: queue.paused,
    };
  } catch {
    return {};
  }
}

export type { PayzheBtpApiMessage };
